import type { FC } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { v4 as uuidv4 } from "uuid";

import Chart from "pro-features/components/Chart/src/Chart";
import { getChartDataViewView } from "pro-features/components/Chart/src/components/column/getChartDataViewView";
import type { RenderCustomLabels } from "pro-features/components/Chart/src/configs/column-chart/columnChartConfig";
import {
    columnChartConfig,
    EChartDisplayType,
} from "pro-features/components/Chart/src/configs/column-chart/columnChartConfig";
import { renderIcon } from "pro-features/components/Chart/src/dataLabels/render-custom-labels/renderIcon";
import type { ColumnChartDataItems } from "pro-features/components/Chart/src/types";
import { percentageSignFilter } from "app/filters/ngFilters";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";

interface IColumnChart {
    chartData: ColumnChartDataItems;
    renderCustomLabels?: RenderCustomLabels;
    columnsToDisplay?: number;
    labelFormatter?: (y: number) => string;
    chartHeight?: number;
    chartDisplayType?: EChartDisplayType;
    maxYaxisValue?: number;
    maxBarWidth?: number;
    displayOthersColumn?: boolean;
}

const CHART_HEIGHT = 240;

const defaultLabelFormatter = (y: number) => {
    return percentageSignFilter()(y, 2);
};
export const ColumnChart: FC<IColumnChart> = (props) => {
    const containerID = useMemo(() => "container-id-" + uuidv4(), []);

    const {
        chartData,
        renderCustomLabels = renderIcon(containerID),
        columnsToDisplay = Infinity,
        labelFormatter = defaultLabelFormatter,
        chartHeight = CHART_HEIGHT,
        chartDisplayType = EChartDisplayType.PERCENTAGE,
        maxYaxisValue,
        maxBarWidth = 48,
        displayOthersColumn = true,
    } = props;

    const chartDataView = useMemo(() => {
        return getChartDataViewView(chartData, columnsToDisplay, labelFormatter, displayOthersColumn);
    }, [chartData]);

    const renderCustomCategories = (index: number) => {
        return chartDataView[index]?.renderLabel?.();
    };

    const chartConfig = useMemo(() => {
        const chartConfig = columnChartConfig({
            renderCustomCategories,
            chartDisplayType,
            displayDataLabelsOnColumns: typeof renderCustomLabels === "function",
            renderCustomLabels,
            height: chartHeight,
            maxYaxisValue,
            maxPointWidth: maxBarWidth,
            marginTop: 40,
        });
        return chartConfig;
    }, [chartDataView]);
    const [key, setKey] = useState(() => Math.random());
    const isMountedRef = useRef(false);
    useEffect(() => {
        if (isMountedRef.current) {
            setKey(Math.random());
        }
        isMountedRef.current = true;
    }, [chartDataView]);
    return (
        <div id={containerID}>
            <Chart
                type={chartTypes.COLUMN}
                data={[{ data: chartDataView }]}
                config={chartConfig}
                key={key}
            />
        </div>
    );
};
